import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';
import { useInvoiceCreation } from '../../hooks/useInvoiceCreation';
import './NewInvoiceDialog.css';

interface NewInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
}

export const NewInvoiceDialog: React.FC<NewInvoiceDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const { phase, invoiceNumber, error, submit, reset } = useInvoiceCreation();

  const handleClose = useCallback(() => {
    setCustomer(null);
    reset();
    onClose();
  }, [onClose, reset]);

  const handleCreate = useCallback(async () => {
    if (!customer) return;
    await submit({ rsCustomerId: Number(customer.repairShoprCustomerId) });
  }, [customer, submit]);

  // Navigate to invoice detail on success
  React.useEffect(() => {
    if (phase === 'succeeded' && invoiceNumber) {
      handleClose();
      navigate(`/invoice/${invoiceNumber}`);
    }
  }, [phase, invoiceNumber, navigate, handleClose]);

  const isWorking = phase === 'submitting' || phase === 'accepted';

  // Escape closes the modal (when the customer lookup popup isn't capturing it).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isWorking) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, isWorking, handleClose]);

  if (!open) return null;

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    // Only close when the press starts on the backdrop itself, not the card.
    if (e.target === e.currentTarget && !isWorking) handleClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="new-invoice-modal" role="dialog" aria-modal="true" aria-label="New Invoice">
        <div className="new-invoice-modal__header">
          <span className="new-invoice-modal__title">New Invoice</span>
          <button
            type="button"
            className="new-invoice-modal__close"
            onClick={handleClose}
            disabled={isWorking}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="new-invoice-modal__body">
          <div className="new-invoice-modal__description">
            Select a customer to create a new invoice.
          </div>

          <CustomerLookupControl
            value={customer}
            onChange={setCustomer}
            autoFocus
            disabled={isWorking}
          />

          {isWorking && (
            <div className="new-invoice-modal__status">
              <CircularProgress size={16} />
              <span>{phase === 'submitting' ? 'Submitting...' : 'Creating invoice...'}</span>
            </div>
          )}

          {error && <div className="new-invoice-modal__error">{error}</div>}
        </div>

        <div className="new-invoice-modal__footer">
          <button
            type="button"
            className="new-invoice-modal__cancel"
            onClick={handleClose}
            disabled={isWorking}
          >
            Cancel
          </button>
          <button
            type="button"
            className="new-invoice-modal__submit"
            onClick={handleCreate}
            disabled={!customer || isWorking}
          >
            Create Invoice
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
